import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, X, Check, Loader2 } from "lucide-react";
import { api } from "@/lib/api.js";

interface CompanyShort {
  idcompany: number;
  name: string;
}

/**
 * Selector de company con búsqueda server-side por NOMBRE o por IDCOMPANY.
 * Reemplaza al <select> plano (que solo cargaba un subconjunto y no permitía
 * buscar por ID). La búsqueda usa /companies?q= que matchea nombre o id exacto.
 */
export function CompanyCombobox({
  value,
  valueName,
  onChange,
  excludeId,
  placeholder = "Busca por nombre o ID…",
  autoFocus = false,
  id,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedby,
  "aria-label": ariaLabel,
}: {
  value: number | null;
  valueName?: string | null;
  onChange: (idcompany: number | null, name: string | null) => void;
  excludeId?: number | null;
  placeholder?: string;
  autoFocus?: boolean;
  id?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
  "aria-label"?: string;
}) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [debounced, setDebounced] = useState("");
  const [selectedName, setSelectedName] = useState<string | null>(
    valueName ?? null,
  );
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => setSelectedName(valueName ?? null), [valueName]);

  // Debounce del término de búsqueda
  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 250);
    return () => clearTimeout(t);
  }, [term]);

  // Cerrar al hacer click fuera
  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const { data, isFetching } = useQuery({
    queryKey: ["company-search", debounced],
    queryFn: () =>
      api.get<{ items: CompanyShort[] }>(
        `/companies?pageSize=50${
          debounced ? `&q=${encodeURIComponent(debounced)}` : ""
        }`,
      ),
    enabled: open,
    staleTime: 30_000,
  });

  const items = useMemo(
    () =>
      (data?.items ?? []).filter((c) =>
        excludeId ? c.idcompany !== excludeId : true,
      ),
    [data, excludeId],
  );

  if (value != null) {
    // Estado seleccionado: muestra chip con la company elegida + botón limpiar
    return (
      <div className="flex items-center gap-2">
        <div className="flex-1 input flex items-center justify-between !py-2">
          <span className="truncate">
            <span className="font-medium">{selectedName ?? "Company"}</span>{" "}
            <span className="mono text-xs text-slate-500">({value})</span>
          </span>
          <Check size={14} className="text-emerald-600 shrink-0" />
        </div>
        <button
          type="button"
          className="btn-ghost p-2"
          title="Cambiar"
          onClick={() => {
            onChange(null, null);
            setSelectedName(null);
            setTerm("");
            setOpen(true);
          }}
        >
          <X size={16} />
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={boxRef}>
      <Search
        size={14}
        className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
      />
      <input
        id={id}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedby}
        aria-label={ariaLabel}
        className="input pl-9"
        placeholder={placeholder}
        value={term}
        autoFocus={autoFocus}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setTerm(e.target.value);
          setOpen(true);
        }}
      />
      {isFetching && (
        <Loader2
          size={14}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin"
        />
      )}
      {open && (
        <div className="absolute z-[60] mt-1 w-full max-h-64 overflow-auto rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg">
          {items.length === 0 && !isFetching && (
            <div className="px-3 py-2 text-sm text-slate-400">
              {debounced
                ? "Sin resultados para esa búsqueda."
                : "Escribe para buscar una company…"}
            </div>
          )}
          {items.map((c) => (
            <button
              key={c.idcompany}
              type="button"
              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-between gap-2"
              onClick={() => {
                onChange(c.idcompany, c.name);
                setSelectedName(c.name);
                setOpen(false);
                setTerm("");
              }}
            >
              <span className="truncate">{c.name}</span>
              <span className="mono text-xs text-slate-500 shrink-0">
                {c.idcompany}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
